import type { PricingPlanProps } from '@nuxt/ui'

export type ProductsLayoutMode = 'default' | 'compact' | 'scale' | 'carousel'

export type BlockProductItem = {
  name: string
  description?: string
  price?: string
  badge?: string
  image?: string
  icon?: string
  href?: string
  ctaLabel?: string
  meta?: string
  highlighted?: boolean
  features?: string[]
}

export function itemFeatureList(item: BlockProductItem): string[] {
  if (!item.features?.length) return []
  return item.features.filter((f) => Boolean(f && String(f).trim()))
}

/** Maps block items to UPricingPlans props (see Nuxt UI pricing examples). */
export function toPricingPlans(
  items: BlockProductItem[],
  options: {
    layout: ProductsLayoutMode
    defaultCta: string
    builderPreview?: boolean
    onNavigate?: (to: string) => void
  },
): PricingPlanProps[] {
  const { layout, defaultCta, builderPreview, onNavigate } = options
  const usesPlanScale =
    layout === 'scale' || layout === 'compact' || layout === 'carousel'

  return items.map((item) => {
    const plan: PricingPlanProps = {
      title: item.name,
      description: item.description,
      price: item.price || undefined,
      features: itemFeatureList(item),
    }

    if (item.badge) {
      plan.badge = item.badge
    }

    if (usesPlanScale && item.highlighted) {
      plan.scale = true
    } else if (item.highlighted) {
      plan.highlight = true
    }

    if (item.href) {
      const label = item.ctaLabel || defaultCta
      if (builderPreview && onNavigate) {
        plan.button = {
          label,
          block: true,
          size: 'lg',
          color: 'primary',
          onClick: () => onNavigate(item.href!),
        }
      } else {
        plan.button = {
          label,
          block: true,
          size: 'lg',
          color: 'primary',
          to: item.href,
        }
      }
    }

    return plan
  })
}

export const PRODUCTS_LAYOUT_OPTIONS: Array<{
  value: ProductsLayoutMode
  label: { fa: string; en: string }
  description: { fa: string; en: string }
}> = [
  {
    value: 'default',
    label: { fa: 'معمولی', en: 'Default' },
    description: { fa: 'کارت‌های جدا با فاصله', en: 'Spaced cards' },
  },
  {
    value: 'compact',
    label: { fa: 'فشرده', en: 'Compact' },
    description: { fa: 'فشرده + کارت محبوب بزرگ‌تر', en: 'Compact + scaled popular card' },
  },
  {
    value: 'scale',
    label: { fa: 'برجسته', en: 'Scale' },
    description: { fa: 'کارت محبوب بزرگ‌تر', en: 'Highlighted plan scaled up' },
  },
  {
    value: 'carousel',
    label: { fa: 'اسلایدر', en: 'Carousel' },
    description: { fa: 'اسکرول افقی (موبایل و دسکتاپ)', en: 'Swiper on all screen sizes' },
  },
]
